/**
 * Exclusion Map Component
 *
 * Interactive map for excluding features from the quiz.
 * Shows all filtered features, click to toggle exclusion.
 */

import { useEffect, useRef, useCallback, useState, memo, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import type { Feature, GeoJsonProperties, Geometry, FeatureCollection } from "geojson";
import type { PathOptions, CircleMarkerOptions } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getFeatureIdentifier } from "@/utils/geojsonLoader";
import { useProcessedCollection } from "@/hooks/useProcessedCollection";

interface ExclusionMapProps {
  collection: FeatureCollection<Geometry, GeoJsonProperties>;
  originalCollection: FeatureCollection<Geometry, GeoJsonProperties>; // Unfiltered collection for correct ID generation
  excludedFeatureIds: string[];
  promptKeys: string[];
  onToggleExclusion: (promptValue: string) => void;
}

// Color constants
const COLOR_INCLUDED = "#3b82f6"; // Blue-500 (included)
const COLOR_EXCLUDED = "#9ca3af"; // Gray-400 (excluded)
const COLOR_BORDER = "#ffffff"; // White border

// Constants for auto-merge
// NOTE: Auto-merge disabled in exclusion map for performance
// Users can still exclude by prompt value without seeing merged geometries
const EMPTY_EXCLUSIONS: string[] = [];
const PROCESSING_OPTIONS = { enableAutoMerge: false };

function ExclusionMapComponent({
  collection,
  originalCollection,
  excludedFeatureIds,
  promptKeys,
  onToggleExclusion,
}: ExclusionMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.GeoJSON | null>(null);
  const hasFitBounds = useRef(false);
  const [mapReady, setMapReady] = useState(false);

  // Apply auto-merge to show merged features (e.g., states instead of districts)
  const processedCollection = useProcessedCollection(
    collection,
    [], // No additional filters (already filtered by Step4ExclusionMap)
    EMPTY_EXCLUSIONS, // Don't remove exclusions, just style them
    promptKeys,
    PROCESSING_OPTIONS
  );

  // Build a set of excluded prompt values from the original collection
  const excludedPromptValues = useMemo(() => {
    if (excludedFeatureIds.length === 0 || !originalCollection || promptKeys.length === 0) {
      return new Set<string>();
    }

    // IMPORTANT: Use original unfiltered collection to match IDs correctly
    const excludedFeatures = originalCollection.features.filter((feature, originalIndex) => {
      const featureId = getFeatureIdentifier(feature, originalIndex);
      return excludedFeatureIds.includes(featureId);
    });

    // Extract prompt values from excluded features
    const promptValuesSet = new Set<string>();
    excludedFeatures.forEach((feature) => {
      if (!feature.properties) return;

      const promptValue = promptKeys
        .map((key) => {
          const value = feature.properties?.[key];
          return Array.isArray(value)
            ? JSON.stringify([...value].sort())
            : JSON.stringify(value);
        })
        .join("||");

      if (promptValue) {
        promptValuesSet.add(promptValue);
      }
    });

    return promptValuesSet;
  }, [originalCollection, excludedFeatureIds, promptKeys]);

  // Create a map of feature -> promptValue for click handling
  const featurePromptValueMap = useMemo(() => {
    const map = new Map<Feature<Geometry, GeoJsonProperties>, string>();
    processedCollection?.features.forEach((feature) => {
      if (!feature.properties || promptKeys.length === 0) return;

      const promptValue = promptKeys
        .map((key) => {
          const value = feature.properties?.[key];
          return Array.isArray(value)
            ? JSON.stringify([...value].sort())
            : JSON.stringify(value);
        })
        .join("||");

      map.set(feature, promptValue);
    });
    return map;
  }, [processedCollection, promptKeys]);

  // Style function for polygons
  const getPolygonStyle = useCallback(
    (feature?: Feature<Geometry, GeoJsonProperties>): PathOptions => {
      if (!feature || !feature.properties || promptKeys.length === 0) {
        return {
          fillColor: COLOR_INCLUDED,
          color: COLOR_BORDER,
          weight: 2,
          opacity: 1,
          fillOpacity: 0.5,
        };
      }

      const promptValue = featurePromptValueMap.get(feature) || "";
      const isExcluded = excludedPromptValues.has(promptValue);

      return {
        fillColor: isExcluded ? COLOR_EXCLUDED : COLOR_INCLUDED,
        color: COLOR_BORDER,
        weight: 2,
        opacity: 1,
        fillOpacity: isExcluded ? 0.3 : 0.5,
      };
    },
    [excludedPromptValues, featurePromptValueMap, promptKeys]
  );

  // Style function for points
  const getPointStyle = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>): CircleMarkerOptions => {
      const promptValue = featurePromptValueMap.get(feature) || "";
      const isExcluded = excludedPromptValues.has(promptValue);

      return {
        radius: 8,
        fillColor: isExcluded ? COLOR_EXCLUDED : COLOR_INCLUDED,
        color: COLOR_BORDER,
        weight: 2,
        opacity: 1,
        fillOpacity: isExcluded ? 0.3 : 0.8,
      };
    },
    [excludedPromptValues, featurePromptValueMap]
  );

  // Event handler for each feature
  const onEachFeature = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>, layer: L.Layer) => {
      if (!(layer instanceof L.Path)) {
        return;
      }

      const promptValue = featurePromptValueMap.get(feature) || "";

      // Click handler - toggle exclusion
      layer.on("click", (e) => {
        L.DomEvent.stopPropagation(e);
        console.log("🗺️ Feature clicked (prompt value):", promptValue);
        onToggleExclusion(promptValue);
      });

      // Hover effect
      layer.on("mouseover", () => {
        layer.setStyle({
          fillOpacity: 0.8,
          weight: 3,
        });
      });

      layer.on("mouseout", () => {
        // Recalculate correct fillOpacity based on current exclusion state
        // Don't use cached value - it becomes stale after toggling!
        const isExcluded = excludedPromptValues.has(promptValue);
        const correctFillOpacity = isExcluded ? 0.3 : 0.5;

        layer.setStyle({
          fillOpacity: correctFillOpacity,
          weight: 2,
        });
      });

      // Add cursor pointer
      layer.on("mouseover", () => {
        if (mapRef.current) {
          const container = mapRef.current.getContainer();
          container.style.cursor = "pointer";
        }
      });

      layer.on("mouseout", () => {
        if (mapRef.current) {
          const container = mapRef.current.getContainer();
          container.style.cursor = "";
        }
      });
    },
    [onToggleExclusion, featurePromptValueMap, excludedPromptValues]
  );

  // Point to layer converter
  const pointToLayer = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>, latlng: L.LatLng) => {
      return L.circleMarker(latlng, getPointStyle(feature));
    },
    [getPointStyle]
  );

  // Fit map to bounds once
  useEffect(() => {
    if (!mapRef.current || !layerRef.current || !processedCollection || processedCollection.features.length === 0 || !mapReady) {
      return;
    }

    if (hasFitBounds.current) {
      return; // Already fitted
    }

    try {
      const bounds = layerRef.current.getBounds();
      if (bounds.isValid()) {
        mapRef.current.fitBounds(bounds, {
          padding: [20, 20],
          maxZoom: 10,
        });
        hasFitBounds.current = true;
      }
    } catch (error) {
      console.error("Error fitting bounds:", error);
    }
  }, [mapReady, processedCollection]);

  // Update layer styles when exclusions change
  useEffect(() => {
    if (!layerRef.current) return;

    layerRef.current.eachLayer((layer) => {
      if (layer instanceof L.Path) {
        const feature = (layer as any).feature as Feature<Geometry, GeoJsonProperties>;
        if (feature) {
          const promptValue = featurePromptValueMap.get(feature) || "";
          const isExcluded = excludedPromptValues.has(promptValue);

          layer.setStyle({
            fillColor: isExcluded ? COLOR_EXCLUDED : COLOR_INCLUDED,
            fillOpacity: isExcluded ? 0.3 : 0.5,
          });
        }
      }
    });
  }, [excludedPromptValues, featurePromptValueMap]);

  return (
    <div className="map-container relative w-full h-full">
      <MapContainer
        center={[0, 0]}
        zoom={2}
        style={{ height: "100%", width: "100%" }}
        whenReady={() => setMapReady(true)}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {processedCollection && (
          <GeoJSON
            key={`geojson-${processedCollection.features.length}`}
            data={processedCollection}
            style={getPolygonStyle}
            onEachFeature={onEachFeature}
            pointToLayer={pointToLayer}
            ref={layerRef}
          />
        )}
      </MapContainer>
    </div>
  );
}

export const ExclusionMap = memo(ExclusionMapComponent);
